import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconBackground, IconSparkles, IconUpload } from '@tabler/icons-react';
import { useArtEditorContext } from '../../../context';
import ImageLibrary from '../../ImageLibrary';
import { createImageLayer } from '../../../../model/factory';
import { addLayer, photoFrameOf, replaceImage, setImageFill, updateLayer } from '../../../../model/operations';

// A gallery image is the creator's upload (an `art_image` asset) or, with `kind: 'library'`,
// a hosted photo (public https URL: catalog templates, the community library).
export const imageSource = image => (image.kind === 'library'
  ? { kind: 'library', asset_id: null, url: image.url, width: image.width, height: image.height }
  : { kind: 'asset', asset_id: image.id, url: image.url, width: image.width, height: image.height });

// Uploads and the host's image library (ImageLibrary: the creator's uploads, the images
// generated in their arts, the community's). A pick adds the photo, swaps the selected one
// in `replace` mode, or fills the selected shape in `fill` mode; "background" lays it
// full-bleed under everything and locks it. With a photo or a frame selected, a host chat
// (`onAskAI`) and the host's `imageGeneration`, "Gerar com IA" scopes the chat to it with a
// prompt to finish. A selected shade stands for the photo frame under it (`photoFrameOf`).
export default function PhotosPanel({ mode, onClose }) {
  const { t } = useTranslation('artEditor');
  const { editor, images, readOnly, uploadImages, onAskAI, imageGeneration } = useArtEditorContext();
  const [added, setAdded] = useState(null);
  const input = useRef(null);
  const doc = editor.document;
  const selected = editor.selected.length === 1 ? photoFrameOf(editor.page, editor.selected[0]) : null;
  // `fill`: the photo of the selected image, shape or text (a text shows it through its letters).
  const fillable = selected && (selected.type === 'image' || selected.type === 'text' || (selected.type === 'shape' && selected.shape !== 'line'));
  const target = mode === 'replace' && selected?.type === 'image' ? selected : mode === 'fill' && fillable ? selected : null;
  const filling = mode === 'fill' && !!target;
  const framed = selected && (selected.type === 'image' || (selected.type === 'shape' && selected.shape !== 'line')) ? selected : null;
  const asking = !!onAskAI && !!imageGeneration && !!framed && !readOnly;

  // A community photo is named: the backend reads a nameless library photo as the template's
  // example (ArtImagePlanner::isPlaceholder) and would offer to replace it in a new layout.
  const community = image => image.kind === 'library';
  const named = (image, layer) => current => (community(image) && !layer.name && layer.type !== 'text' ? updateLayer(current, layer.id, { name: t('communityPhoto') }) : current);
  const pick = image => {
    if (!doc) return;
    if (filling) { editor.apply(current => named(image, target)(setImageFill(current, target.id, imageSource(image))), { label: 'fill' }); onClose?.(); return; }
    if (target) { editor.apply(current => named(image, target)(replaceImage(current, target.id, imageSource(image))), { label: 'replace' }); onClose?.(); return; }
    editor.add({ ...createImageLayer(doc, imageSource(image)), ...(community(image) ? { name: t('communityPhoto') } : {}) });
    onClose?.();
  };
  const background = image => {
    if (!doc) return;
    const layer = { ...createImageLayer(doc, imageSource(image), { binding: 'photo' }), x: 0, y: 0, width: doc.width, height: doc.height, locked: true, name: t('backgroundPhoto') };
    editor.apply(current => ({ doc: addLayer(current, editor.page.id, layer, 0), select: [layer.id] }), { label: 'background' });
    onClose?.();
  };
  const upload = async files => {
    const uploaded = await uploadImages(files, { target });
    if (!uploaded.length) return;
    setAdded(uploaded);
    onClose?.();
  };
  const askAI = () => {
    onAskAI([framed], { prompt: t('generateImagePrompt') });
    onClose?.();
  };

  return <>
    <section className="art-panel-section">
      {target && <Text.caption contrast={6}>{t(filling ? 'fillHint' : 'replaceHint')}</Text.caption>}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" multiple={!target} hidden onChange={event => { const files = [...event.target.files]; event.target.value = ''; if (files.length) upload(files); }} />
      <Button fullWidth color="inverse" variant="rounded" startIcon={<IconUpload />} disabled={readOnly || !images?.upload} preventLoading onClick={() => input.current?.click()}>
        {t('uploadPhoto')}
      </Button>
      {asking && <Button fullWidth color="ai" variant="rounded" startIcon={<IconSparkles />} preventLoading onClick={askAI}>{t('generateImage')}</Button>}
      <Text.caption contrast={5}>{t(asking ? 'generateImageHint' : 'uploadHint')}</Text.caption>
    </section>
    {images?.list && <ImageLibrary source={images} onPick={pick} added={added} disabled={readOnly} columns={3}
      pickLabel={t(filling ? 'fillWith' : target ? 'replaceWith' : 'addPhoto')}
      tileAction={target ? null : image => <Button className="art-image-grid__action" size="micro" color="inverse" variant="rounded" preventLoading icon={<IconBackground />}
        disabled={readOnly} aria-label={t('useAsBackground')} tooltip={t('useAsBackground')} onClick={() => background(image)} />} />}
  </>;
}
